/**
 * 清理无效的 media 记录
 * 查找 media 表中存储的记录，但在 items 表中没有对应记录的，然后删除这些 media 记录
 * 
 * 更新：现在支持用户独立数据库 IN_{uuid}
 */

import { openUserDB } from '../services/dbManager'

const STORE_MEDIA = 'media'
const STORE_ITEMS = 'items'

/**
 * 从 fileId 或 imageId 中提取原始 UUID（去除扩展名）
 */
function extractFileId(fileId: string | undefined | null): string | null {
  if (!fileId) return null
  
  // 如果包含扩展名，提取原始UUID
  if (fileId.includes('.')) {
    return fileId.substring(0, fileId.lastIndexOf('.'))
  }
  
  return fileId
}

/**
 * 收集所有 items 中使用的 fileId/imageId
 */
async function collectUsedFileIds(db: IDBDatabase): Promise<Set<string>> {
  return new Promise((resolve, reject) => {
    const usedFileIds = new Set<string>()
    
    try {
      if (!db.objectStoreNames.contains(STORE_ITEMS)) {
        resolve(usedFileIds)
        return
      }
      
      const itemsStore = db.transaction([STORE_ITEMS], 'readonly').objectStore(STORE_ITEMS)
      const request = itemsStore.openCursor()
      
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result
        if (cursor) {
          const item = cursor.value
          
          // 收集 fileId
          if (item.fileId) {
            const fileId = extractFileId(item.fileId)
            if (fileId) {
              usedFileIds.add(fileId)
            }
          }
          
          // 收集 imageId（兼容旧数据）
          if (item.imageId) {
            const imageId = extractFileId(item.imageId)
            if (imageId) {
              usedFileIds.add(imageId)
            }
          }
          
          // 收集 thumbnailFileId
          if (item.thumbnailFileId) {
            const thumbnailId = extractFileId(item.thumbnailFileId)
            if (thumbnailId) {
              usedFileIds.add(thumbnailId)
            }
          }
          
          cursor.continue()
        } else {
          // 遍历完成
          resolve(usedFileIds)
        }
      }
      
      request.onerror = () => reject(request.error)
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * 收集所有 projects 中使用的 fileId（封面图等）
 */
async function collectProjectFileIds(db: IDBDatabase): Promise<Set<string>> {
  return new Promise((resolve, reject) => {
    const usedFileIds = new Set<string>()
    
    try {
      if (!db.objectStoreNames.contains('projects')) {
        resolve(usedFileIds)
        return
      }
      
      const projectsStore = db.transaction(['projects'], 'readonly').objectStore('projects')
      const request = projectsStore.openCursor()
      
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result
        if (cursor) {
          const project = cursor.value
          
          // 收集封面图 fileId
          if (project.coverImage) {
            const fileId = extractFileId(project.coverImage)
            if (fileId) {
              usedFileIds.add(fileId)
            }
          }
          
          // 收集封面缩略图 fileId
          if (project.coverThumbnailFileId) {
            const fileId = extractFileId(project.coverThumbnailFileId)
            if (fileId) {
              usedFileIds.add(fileId)
            }
          }
          
          cursor.continue()
        } else {
          resolve(usedFileIds)
        }
      }
      
      request.onerror = () => reject(request.error)
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * 清理无效的 media 记录
 * @param userUuid 用户 UUID
 * @returns 返回清理结果 { total: 总数, deleted: 删除数量, failed: 失败数量 }
 */
export async function cleanupInvalidMedia(userUuid: string): Promise<{
  total: number
  deleted: number
  failed: number
  deletedIds: string[]
}> {
  const result = {
    total: 0,
    deleted: 0,
    failed: 0,
    deletedIds: [] as string[]
  }

  if (!userUuid) {
    console.warn('未提供用户 UUID')
    return result
  }

  let db: IDBDatabase | null = null
  
  try {
    // 打开用户数据库
    db = await openUserDB(userUuid)
    
    // 检查表是否存在
    if (!db.objectStoreNames.contains(STORE_MEDIA)) {
      console.warn('media 表不存在')
      return result
    }
    
    if (!db.objectStoreNames.contains(STORE_ITEMS)) {
      console.warn('items 表不存在')
      return result
    }

    // 收集所有使用的 fileId
    const usedFileIdsFromItems = await collectUsedFileIds(db)
    
    // 收集项目中的 fileId
    const usedFileIdsFromProjects = await collectProjectFileIds(db)
    
    // 合并所有使用的 fileId
    const allUsedFileIds = new Set<string>()
    usedFileIdsFromItems.forEach(id => allUsedFileIds.add(id))
    usedFileIdsFromProjects.forEach(id => allUsedFileIds.add(id))

    console.log(`找到 ${allUsedFileIds.size} 个正在使用的 fileId`)

    // 获取所有 media 记录
    const mediaStore = db.transaction([STORE_MEDIA], 'readonly').objectStore(STORE_MEDIA)
    const allMedia = await new Promise<any[]>((resolve, reject) => {
      const request = mediaStore.getAll()
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })

    result.total = allMedia.length
    console.log(`media 表中共有 ${result.total} 条记录`)

    // 找出无效的记录
    const invalidMediaIds: string[] = []
    allMedia.forEach((media) => {
      const mediaId = extractFileId(media.id)
      if (mediaId && !allUsedFileIds.has(mediaId)) {
        invalidMediaIds.push(media.id) // 使用原始 id（可能包含扩展名）
      }
    })

    console.log(`找到 ${invalidMediaIds.length} 条无效的 media 记录`)

    // 删除无效记录
    if (invalidMediaIds.length > 0) {
      const deleteStore = db.transaction([STORE_MEDIA], 'readwrite').objectStore(STORE_MEDIA)
      
      for (const id of invalidMediaIds) {
        try {
          await new Promise<void>((resolve, reject) => {
            const request = deleteStore.delete(id)
            request.onsuccess = () => resolve()
            request.onerror = () => reject(request.error)
          })
          result.deleted++
          result.deletedIds.push(id)
        } catch (error) {
          console.error(`删除 media 记录失败 (id: ${id}):`, error)
          result.failed++
        }
      }
    }

    console.log(`清理完成: 总计 ${result.total} 条，删除 ${result.deleted} 条，失败 ${result.failed} 条`)
    
    return result
  } catch (error) {
    console.error('清理无效 media 记录失败:', error)
    throw error
  } finally {
    if (db) {
      db.close()
    }
  }
}
